import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash-es';
import { createSearchParams } from 'react-router';

import type {
  InvoiceExportCondensedReport,
  InvoicePaymentRow,
  InvoiceRefundRow,
} from './types';
import type { HydratedInvoice } from './useHydratedInvoice';
import type {
  FilterGroup,
  InvoiceRead,
  OverallPaymentStatusEnum,
} from 'src/common/external/bambi-api/bambiApi';
import type { NumberRangeValue } from 'src/common/primitives/NumberRangeFilterPopover/NumberRangeFilterPopover';
import { clearFiltersExtraReducer } from 'src/common/redux/clearFiltersExtraReducer';
import { searchParamsToJSON } from 'src/common/util/searchParamsToJSON';

export type ExportOptions = 'quickbooks' | 'condensed';
export type TriStateFilter = 'yes' | 'no' | 'any';
// Which steps the condensed PDF export wizard should show:
// - 'full': gather filters (date range, payers, etc.) then generate
// - 'skipToGeneration': data was already fetched, go straight to generation
// - 'promptSort': ask for a sort order, fetch, then generate
export type ExportFlowStep = 'full' | 'skipToGeneration' | 'promptSort';
// Which flow triggered a condensed PDF export, so the generation options
// form can conditionally show flow-specific options (e.g. Include QR Code
// only applies to the payment link flow).
export type ExportContext = 'default' | 'paymentLink';

interface InvoiceState {
  editingInvoice?: InvoiceRead | null;
  showInvoiceModal: boolean;
  showPayModal: boolean;
  hydratedInvoice?: HydratedInvoice | null;
  highlightPaymentId?: string;
  // editingLineItemGroup is the trip id
  editingLineItemGroup?: string;
  exportedCondensedData?: InvoiceExportCondensedReport;
  showExportInvoicesModal: boolean;
  exportInvoicesReportType?: ExportOptions;
  exportFlowStep: ExportFlowStep;
  exportInvoiceIds?: string[];
  selectedInvoiceRows: Record<string, boolean>;
  exportedInvoicePaymentsData?: InvoicePaymentRow[];
  exportedInvoiceRefundsData?: InvoiceRefundRow[];
  editingPaymentLinkId?: string;
  showPaymentLinkModal: boolean;
  showInvoiceEmailModal: boolean;
  invoiceExportMode: 'BLOB' | 'DOWNLOAD';
  invoiceExportBlobUrl?: string;
  exportContext: ExportContext;

  // Filters
  filterDateStart?: string;
  filterDateEnd?: string;
  filterHasPayments?: boolean;
  filterSearchTerm?: string;
  filterTripPayers?: string[];
  filterPaymentStatus?: OverallPaymentStatusEnum[];
  filterTotalAmount?: NumberRangeValue;
  filterTotalAmountDue?: NumberRangeValue;
  filterGroup?: FilterGroup;
  filterHasPaymentLink: TriStateFilter;
  filterHasEmailedPaymentLink: TriStateFilter;
}

export const initialState: InvoiceState = {
  editingInvoice: null,
  hydratedInvoice: null,
  showInvoiceModal: false,
  showPayModal: false,
  editingLineItemGroup: '',
  highlightPaymentId: '',
  showExportInvoicesModal: false,
  exportFlowStep: 'full',
  selectedInvoiceRows: {},
  editingPaymentLinkId: '',
  showPaymentLinkModal: false,
  showInvoiceEmailModal: false,
  invoiceExportMode: 'DOWNLOAD',
  exportContext: 'default',
  filterHasPaymentLink: 'any',
  filterHasEmailedPaymentLink: 'any',
};

export const invoiceSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    editInvoice(state, action: PayloadAction<InvoiceRead | null>) {
      state.editingInvoice = action.payload;
    },
    setHydratedInvoice(state, action: PayloadAction<HydratedInvoice | null>) {
      state.hydratedInvoice = action.payload;
    },
    setInvoiceModalOpen(state, action: PayloadAction<boolean>) {
      state.showInvoiceModal = action.payload;
    },
    editLineItems(state, action: PayloadAction<string>) {
      state.editingLineItemGroup = action.payload;
    },
    setPayModalOpen(state, action: PayloadAction<boolean>) {
      state.showPayModal = action.payload;
    },
    resetEditing(state) {
      state.editingLineItemGroup = '';
      state.editingInvoice = null;
      state.showInvoiceModal = false;
    },
    setFilterDateRange(
      state,
      action: PayloadAction<{ rangeStart?: string; rangeEnd?: string }>
    ) {
      state.filterDateStart = action.payload.rangeStart;
      state.filterDateEnd = action.payload.rangeEnd;
    },
    setFilterHasPayments(state, action: PayloadAction<boolean>) {
      state.filterHasPayments = action.payload;
    },
    setFilterSearchTerm(state, action: PayloadAction<string>) {
      state.filterSearchTerm = action.payload;
    },
    setFilterTripPayers(state, action: PayloadAction<string[]>) {
      state.filterTripPayers = action.payload;
    },
    setFilterPaymentStatus(
      state,
      action: PayloadAction<OverallPaymentStatusEnum[]>
    ) {
      state.filterPaymentStatus = action.payload;
    },
    setFilterTotalAmount(state, action: PayloadAction<NumberRangeValue>) {
      state.filterTotalAmount = action.payload;
    },
    setFilterTotalAmountDue(state, action: PayloadAction<NumberRangeValue>) {
      state.filterTotalAmountDue = action.payload;
    },
    setHighlightPaymentId(state, action: PayloadAction<string | undefined>) {
      state.highlightPaymentId = action.payload;
    },
    setExportedCondensedReportData(
      state,
      action: PayloadAction<InvoiceExportCondensedReport>
    ) {
      state.exportedCondensedData = action.payload;
    },
    setShowExportInvoicesModal(state, action: PayloadAction<boolean>) {
      state.showExportInvoicesModal = action.payload;
    },
    setExportInvoicesReportType(
      state,
      action: PayloadAction<ExportOptions | undefined>
    ) {
      state.exportInvoicesReportType = action.payload;
    },
    setExportFlowStep(state, action: PayloadAction<ExportFlowStep>) {
      state.exportFlowStep = action.payload;
    },
    setExportInvoiceIds(state, action: PayloadAction<string[] | undefined>) {
      state.exportInvoiceIds = action.payload;
    },
    setSelectedInvoiceRows(
      state,
      action: PayloadAction<Record<string, boolean>>
    ) {
      state.selectedInvoiceRows = action.payload;
    },
    setExportedInvoicePaymentsData(
      state,
      action: PayloadAction<InvoicePaymentRow[]>
    ) {
      state.exportedInvoicePaymentsData = action.payload;
    },
    setExportedInvoiceRefundsData(
      state,
      action: PayloadAction<InvoiceRefundRow[]>
    ) {
      state.exportedInvoiceRefundsData = action.payload;
    },
    setEditingPaymentLinkId(state, action: PayloadAction<string | undefined>) {
      state.editingPaymentLinkId = action.payload;
    },
    setShowPaymentLinkModal(state, action: PayloadAction<boolean>) {
      state.showPaymentLinkModal = action.payload;
    },
    setShowInvoiceEmailModal(state, action: PayloadAction<boolean>) {
      state.showInvoiceEmailModal = action.payload;
    },
    setInvoiceExportMode(state, action: PayloadAction<'BLOB' | 'DOWNLOAD'>) {
      state.invoiceExportMode = action.payload;
    },
    setInvoiceExportBlobUrl(state, action: PayloadAction<string>) {
      state.invoiceExportBlobUrl = action.payload;
    },
    setExportContext(state, action: PayloadAction<ExportContext>) {
      state.exportContext = action.payload;
    },
    setFilterHasPaymentLink(state, action: PayloadAction<TriStateFilter>) {
      state.filterHasPaymentLink = action.payload;
    },
    setFilterHasEmailedPaymentLink(
      state,
      action: PayloadAction<TriStateFilter>
    ) {
      state.filterHasEmailedPaymentLink = action.payload;
    },
    clearFilters(state) {
      state.filterDateStart = undefined;
      state.filterDateEnd = undefined;
      state.filterHasPayments = undefined;
      state.filterTripPayers = undefined;
      state.filterPaymentStatus = undefined;
      state.filterTotalAmount = undefined;
      state.filterTotalAmountDue = undefined;
      state.filterGroup = undefined;
      state.filterHasPaymentLink = 'any';
      state.filterHasEmailedPaymentLink = 'any';
    },
    // Translates ?search into state
    initializeFilters(state) {
      const currentParams = searchParamsToJSON(
        new URLSearchParams(window.location.search)
      );

      Object.keys(currentParams).forEach((param) => {
        const rawValue = currentParams[param];
        const value: unknown = (() => {
          switch (param) {
            case 'DateRange': {
              const range = JSON.parse(rawValue as string);

              // Specially handling this value since its a compound one
              state.filterDateStart = range.rangeStart;
              state.filterDateEnd = range.rangeEnd;

              return undefined;
            }
            case 'TotalAmount':
            case 'TotalAmountDue':
              return JSON.parse(rawValue as string);
            case 'TripPayers':
            case 'PaymentStatus':
              return [rawValue].flatMap((n) => n);
            case 'HasPayments':
              return rawValue === '1'
                ? true
                : rawValue === '0'
                  ? false
                  : undefined;
          }
        })();

        if (!value) {
          return state;
        }

        // There isn't a way to validate this state since it gets
        // generated outside of application
        // @ts-ignore
        state[`filter${param}`] = value;
      });
    },
    setSelectedFilterGroup(state, action: PayloadAction<FilterGroup>) {
      const payload = action.payload;
      state.filterGroup = payload;

      const currentParams = searchParamsToJSON(
        new URLSearchParams(window.location.search)
      );

      const nextParams = createSearchParams({
        ordering: get(currentParams, 'ordering', ''),
        filterGroup: get(payload, 'slug', ''),
      });

      window.history.pushState(
        null,
        '',
        `${window.location.pathname}?${nextParams.toString()}`
      );

      // Translate filter group into filters
      Object.keys(payload.filters).forEach((filterName) => {
        // @ts-ignore
        const filterValue = payload.filters[filterName] ?? '';

        switch (filterName) {
          case 'has_payment':
            state.filterHasPayments = filterValue as boolean;
            break;
          case 'range_start':
            state.filterDateStart = filterValue as string;
            break;
          case 'range_end':
            state.filterDateEnd = filterValue as string;
            break;
          case 'trip_payer__in':
            state.filterTripPayers = filterValue as string[];
            break;
          case 'overall_payment_status__in':
            state.filterPaymentStatus =
              filterValue as OverallPaymentStatusEnum[];
            break;
          case 'total_amount_min':
            state.filterTotalAmount = {
              ...(state.filterTotalAmount ?? {}),
              min: filterValue as number,
            };
            break;
          case 'total_amount_max':
            state.filterTotalAmount = {
              ...(state.filterTotalAmount ?? {}),
              max: filterValue as number,
            };
            break;
          case 'due_min':
            state.filterTotalAmountDue = {
              ...(state.filterTotalAmountDue ?? {}),
              min: filterValue as number,
            };
            break;
          case 'due_max':
            state.filterTotalAmountDue = {
              ...(state.filterTotalAmountDue ?? {}),
              max: filterValue as number,
            };
            break;
        }
      });
    },
  },
  extraReducers: (builder) => {
    clearFiltersExtraReducer(builder);

    // Translates setFilterX payload into ?search
    builder.addMatcher(
      (action) => {
        const type: string = action.type;
        return type.includes('setFilter');
      },
      (state, action: PayloadAction) => {
        const type: string = action.type;
        const payload: unknown = action.payload;
        const filterParam = type.match(/setFilter([a-z]+)/i)?.[1];

        if (!filterParam) {
          return state;
        }

        const value: string | string[] | undefined = (() => {
          switch (filterParam) {
            case 'DateRange':
            case 'TotalAmount':
            case 'TotalAmountDue':
              return JSON.stringify(payload);
            case 'HasPayments':
              return (payload as boolean) ? '1' : '0';
            case 'TripPayers':
            case 'PaymentStatus':
              return payload as string[];
          }
        })();

        if (value) {
          const currentParams = searchParamsToJSON(
            new URLSearchParams(window.location.search)
          );
          const nextParams = createSearchParams({
            ...currentParams,
            [filterParam]: value,
          });

          window.history.pushState(
            null,
            '',
            `${window.location.pathname}?${nextParams.toString()}`
          );
        }

        return state;
      }
    );
  },
});

export const {
  editInvoice,
  setInvoiceModalOpen,
  editLineItems,
  resetEditing,
  setPayModalOpen,
  setFilterDateRange,
  setFilterHasPayments,
  setFilterSearchTerm,
  setFilterTripPayers,
  setFilterPaymentStatus,
  setFilterTotalAmount,
  setFilterTotalAmountDue,
  clearFilters,
  initializeFilters,
  setSelectedFilterGroup,
  setHydratedInvoice,
  setHighlightPaymentId,
  setExportedCondensedReportData,
  setShowExportInvoicesModal,
  setExportInvoicesReportType,
  setExportFlowStep,
  setExportInvoiceIds,
  setSelectedInvoiceRows,
  setExportedInvoicePaymentsData,
  setExportedInvoiceRefundsData,
  setEditingPaymentLinkId,
  setShowPaymentLinkModal,
  setShowInvoiceEmailModal,
  setInvoiceExportMode,
  setInvoiceExportBlobUrl,
  setExportContext,
  setFilterHasPaymentLink,
  setFilterHasEmailedPaymentLink,
} = invoiceSlice.actions;
